import React, { MouseEventHandler } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface MobileAppPromoHeaderProps {
  title: string;
  description: string;
  isExpanded: boolean;
  bodyId: string;
  onClick: MouseEventHandler<HTMLButtonElement> | undefined;
}

const MobileAppPromoHeaderTitle = styled(Typography)({
  fontSize: '0.9643rem',
  fontWeight: 700,
  color: '#1a2636',
});

const MobileAppPromoHeaderSubtitle = styled(Typography)({
  fontSize: '0.8214rem',
  color: '#66727e',
});

const MobileAppPromoHeaderRoot = styled(ButtonBase)({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '14px 20px',
  background: 'linear-gradient(90deg, #f0f8ff 0%, #fff 100%)',
  borderBottom: '1px solid #e0e6ec',
  cursor: 'pointer',
  userSelect: 'none',
  textAlign: 'left',
});

const MobileAppPromoHeaderLeft = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
});

/** Rounded app-icon image displayed at the start of the header row. */
const MobileAppPromoHeaderIconBox = styled('img')({
  width: '2.2857rem', // 32px
  height: '2.2857rem', // 32px
  borderRadius: '0.5714rem', // 8px
  display: 'block',
  objectFit: 'cover',
  flexShrink: 0,
});

const MobileAppPromoHeaderTitleStack = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

const MobileAppPromoToggleRoot = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  color: '#5E7782',
  padding: '4px',
  flexShrink: 0,
});

const MobileAppPromoToggleLabel = styled(Typography)({
  fontSize: '0.78571rem', // 11px
  fontWeight: 600,
  letterSpacing: '0.3px',
  color: 'inherit',
  userSelect: 'none',
  textTransform: 'uppercase',
});

const MobileAppPromoHeader = ({ title, description, isExpanded, bodyId, onClick }: MobileAppPromoHeaderProps) => {
  return (
    <MobileAppPromoHeaderRoot
      onClick={onClick}
      aria-expanded={isExpanded}
      aria-controls={bodyId}
      aria-label="Toggle mobile app download guide"
    >
      <MobileAppPromoHeaderLeft>
        <MobileAppPromoHeaderIconBox
          src="https://static.secur.space/icons/gate-check-app-icon.png"
          alt="SecurSpace Gate Check app icon"
        />
        <MobileAppPromoHeaderTitleStack>
          <MobileAppPromoHeaderTitle variant="h5">{title}</MobileAppPromoHeaderTitle>
          <MobileAppPromoHeaderSubtitle marginTop="0.0714rem">
            {description}
          </MobileAppPromoHeaderSubtitle>
        </MobileAppPromoHeaderTitleStack>
      </MobileAppPromoHeaderLeft>
      <MobileAppPromoToggleRoot aria-hidden="true">
        <MobileAppPromoToggleLabel>{isExpanded ? 'Hide' : 'Show'}</MobileAppPromoToggleLabel>
        <ExpandMoreIcon
          sx={{
            fontSize: '1.2857rem', // 18px
            color: 'inherit',
            transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
          }}
        />
      </MobileAppPromoToggleRoot>
    </MobileAppPromoHeaderRoot>
  );
};

export default MobileAppPromoHeader;
